<template>
  <v-container fluid>
    <PageHeaderSlot>
      <template slot="top-right-slot">
        <!-- 生成匯入CSV -->
        <FormButton v-if="enableDynamicField" text="genImportCSV" class="ml-2" @click="genImportCVS()" />

        <!-- 修改學生表格  -->
        <FormButton v-if="enableDynamicField" text="editStudentForm" class="ml-2" @click="handleEditStudentForm()" />

        <!-- 學生升級 -->
        <FormButton v-if="canUpgrade" text="upgradeStudent" class="ml-2" @click="handleUpgradeStudent()">
          <template slot="prepend">
            <v-icon size="20" class="mr-3">{{ icon.mdiArrowUpCircleOutline }}</v-icon>
          </template>
        </FormButton>

        <!-- 新增學生 -->
        <FormButton v-if="canCreate" text="createStudent" isAdd class="ml-2" :to="{ name: 'CreateStudent' }" />
      </template>
    </PageHeaderSlot>

    <div class="text-subtitle-1 mb-2">{{ $t('currentStudentCount') }}{{ activeStudentTotal }}</div>
    <div class="text-subtitle-1 mb-2">{{ $t('quitStudentCount') }}{{ quitStudentTotal }}</div>

    <Datatable
      :isLoading="tableLoading"
      :tableHeaders="tableHeaders"
      :tableData="tableData"
      :page.sync="filterSelected.page"
      :pageLimit.sync="filterSelected.limit"
      :itemTotal="tableItemTotal"
      enableSearch
      @search-clicked="searchKeyword($event)"
      @search-input="filterSelected.keyword = $event"
      enableClearAll
      @all-search-cleared="clearSearch()"
      @refresh-clicked="refreshData()"
      enableRefresh
      @options-update="onTableOptionsChange($event)"
      enableExport
      @export-clicked="exportData()"
      :exporting="exportLoading"
      actionViewRouteLink="StudentDetails"
      enableAdvancedSearch
      @advanced-search-clicked="handleAdvancedSearch($event)"
      enableMultiTypeSearch
      :searchTypeOptions="searchTypeOptions"
      :searchType.sync="filterSelected.searchType"
      :initSearchKeyword="$route.query.search"
      :enableImport="canCreate"
      @import-clicked="importData($event)"
      enableSort
      :defaultSortKey="filter.sortBy"
      :defaultSortDesc="filter.sortDesc"
    >
      <!-- T01 -->
      <template v-slot:[`item.score`]="{ item }">
        <div v-if="item.score && isAppMode(['T01'])" :class="`belt-color ${item.score}`"></div>
      </template>

      <template v-slot:[`item.avatar`]="{ item }">
        <v-avatar size="50" v-if="item.avatar">
          <img :src="`${$mediaPath}/${item.avatar}`" alt="" />
        </v-avatar>
      </template>

      <template slot="append-toolbar-left">
        <FormSelect
          :fieldValue.sync="filterSelected.status"
          :options="statusOptions"
          hideDetails
          placeholder="enrollStatus"
          enableClear
          @changed="search()"
          class="mr-3 mt-2 mb-3 search-select-field"
          enableTooltip
          :readonly="tableLoading"
        />
      </template>
    </Datatable>

    <!-- Modal：進階搜尋 -->
    <FormDialog
      ref="advancedFormDialog"
      formRef="advancedForm"
      title="table.advanceSearch"
      confirmBtnText="table.search"
    >
      <v-row class="ma-0">
        <v-col cols="12" sm="6" v-if="!this.gDisableStudentGrade">
          <FormSelect
            label="grade"
            :fieldValue.sync="filter.grade"
            :options="gradeOptions"
            placeholder="selectGrade"
            hideDetails
            enableClear
          />
        </v-col>
        <v-col cols="12" sm="6" v-if="!isAppMode(['T27'])">
          <FormInput label="studentSchool" :fieldValue.sync="filter.school" placeholder="studentSchool" hideDetails />
        </v-col>
        <v-col cols="12" sm="6">
          <FormSelect
            label="birthdayMonth"
            :fieldValue.sync="filter.birth"
            :options="birthMonthOptions"
            placeholder="birthdayMonth"
            hideDetails
            enableClear
          />
        </v-col>
        <v-col cols="12" sm="6">
          <FormDatePicker
            label="regYearMonth"
            placeholder="selectRegYearMonth"
            hideDetails
            :dateValue.sync="filter.regYearMonth"
            isYearMonthOnly
            disabledActionButtons
          />
        </v-col>
      </v-row>
    </FormDialog>

    <!-- Modal：修改學生表格 -->
    <FormDialog ref="editStudentFormDialog" formRef="editStudentForm" title="editStudentForm">
      <FormButton text="add" isAdd class="mb-4" @click="addNewField()" />
      <draggable v-model="fieldFormList" :disabled="$store.getters.isLoading">
        <v-card v-for="item in fieldFormList" class="mb-2" outlined :key="`field_${item.id}`">
          <v-card-text class="d-flex align-start py-2 px-2">
            <v-icon size="24" color="colorBlack" style="cursor: grab;" class="mt-1">
              {{ icon.mdiDragHorizontalVariant }}
            </v-icon>
            <div class="flex-grow-1">
              <v-row class="ma-0">
                <v-col class="py-2" cols="12" sm="2">
                  <FormSelect
                    label="type"
                    placeholder="type"
                    :fieldValue.sync="item.field_type"
                    :options="fieldTypeOptions"
                    required
                    hideDetails
                    :disabled="$validate.DataValid(item.field_key)"
                  />
                </v-col>
                <v-col class="py-2" cols="12" sm="5">
                  <FormInput
                    label="titleZH"
                    placeholder="titleZH"
                    :fieldValue.sync="item.field_name_zh"
                    required
                    hideDetails
                  />
                </v-col>
                <v-col class="py-2" cols="12" sm="5">
                  <FormInput
                    label="titleEN"
                    placeholder="titleEN"
                    :fieldValue.sync="item.field_name_en"
                    required
                    hideDetails
                  />
                </v-col>
              </v-row>

              <div v-if="item.field_type === 'dropdown'" class="px-3 my-4">
                <div class="d-flex align-center mb-4">
                  <FormButton text="addOption" isAdd @click="addNewOption(item.id)" />
                  <v-checkbox
                    v-model="item.is_multiple"
                    :label="$t('multipleSelect')"
                    color="primary"
                    hide-details
                    class="mt-0 ml-4 upgrade-checkbox"
                    :disabled="$validate.DataValid(item.field_key)"
                  ></v-checkbox>
                </div>

                <draggable
                  v-if="$validate.DataValid(item.options, true)"
                  v-model="item.options"
                  :disabled="$store.getters.isLoading"
                >
                  <div class="d-flex align-start mb-2" v-for="option in item.options">
                    <v-icon size="24" color="colorBlack" class="mt-5" style="cursor: grab;">
                      {{ icon.mdiDragHorizontalVariant }}
                    </v-icon>
                    <v-row class="flex-grow-1 mx-1 my-0">
                      <v-col cols="12" sm="6">
                        <FormInput
                          placeholder="optionContentZH"
                          :fieldValue.sync="option.option_name_zh"
                          required
                          hideDetails
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <FormInput
                          placeholder="optionContentEN"
                          :fieldValue.sync="option.option_name_en"
                          required
                          hideDetails
                        />
                      </v-col>
                    </v-row>
                    <v-btn
                      text
                      icon
                      min-width="max-content"
                      :ripple="false"
                      @click="removeOption(item.id, option.id)"
                      class="mt-3"
                    >
                      <v-icon class="fi fi-ss-cross-circle" color="red" size="24"></v-icon>
                    </v-btn>
                  </div>
                </draggable>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="d-flex align-center justify-end pb-2">
            <v-checkbox
              v-model="item.is_required"
              :label="$t('required')"
              color="primary"
              hide-details
              class="mt-0 mr-4 upgrade-checkbox"
            ></v-checkbox>
            <FormButton text="delete" color="error" @click="removeField(item.id)" />
          </v-card-actions>
        </v-card>
      </draggable>
    </FormDialog>
  </v-container>
</template>

<script>
import { mdiArrowUpCircleOutline, mdiDragHorizontalVariant } from '@mdi/js'
import Datatable from '@/components/Datatable.vue'
import ExcelMixin from '@/mixins/ExcelMixin.vue'
import FormSelect from '@/components/formField/FormSelect.vue'
import FormDatePicker from '@/components/formField/FormDatePicker.vue'
import FormInput from '@/components/formField/FormInput.vue'
import FormDialog from '@/components/FormDialog.vue'
import fields from './components/fields'
import draggable from 'vuedraggable'

export default {
  name: 'StudentList',
  inject: ['getConfirmDialogRef'],
  mixins: [ExcelMixin],
  components: {
    Datatable,
    FormSelect,
    FormDatePicker,
    FormInput,
    FormDialog,
    draggable,
  },
  computed: {
    confirmRef() {
      return this.getConfirmDialogRef()
    },
    tableHeaders() {
      const vm = this
      let headers = [
        ...(this.isAppMode(['T01']) ? [{ value: 'avatar', text: vm.$t('avatar'), sortable: false }] : []),
        ...(this.isAppMode(['T01']) ? [{ value: 'score', text: vm.$t('belt'), sortable: false }] : []),
        { value: 'student_code', text: vm.$t('studentCode') },
        ...(!this.gDisableStudentGrade ? [{ value: 'grade', text: vm.$t('grade'), sortable: false }] : []),
        ...(this.isAppMode(['T01']) ? [{ value: 'age', text: vm.$t('age') }] : []),
        { value: 'display_student_name', text: vm.$t('name') },
        ...(!this.isAppMode(['T27'])
          ? [{ value: 'student_school_name', text: vm.$t('studentSchool'), sortable: false }]
          : []),
        { value: 'phone', text: vm.$t('phone'), sortable: false },
        { value: 'reg_date', text: vm.$t('regDate') },
        ...(this.isAppMode(['T21']) ? [{ value: 'center_name', text: vm.$t('campus') }] : []),
      ]

      if (this.$validate.allowAction('StudentDetails', this.userType)) {
        headers.push({ value: 'actionView', text: '', align: 'end', sortable: false })
      }

      return headers
    },
    searchTypeOptions() {
      const vm = this
      let options = [
        { value: 'name', text: vm.$t('studentName') },
        { value: 'phone', text: vm.$t('phone') },
      ]

      if (this.isAppMode(['T40'])) {
        options.unshift({ value: 'student_code', text: vm.$t('studentCode') })
      } else {
        options.push({ value: 'student_code', text: vm.$t('studentCode') })
      }
      return options
    },
    birthMonthOptions() {
      const options = []
      const d = new Date()
      d.setDate(1)
      for (let i = 0; i < 12; i++) {
        d.setMonth(i)
        options.push({ value: `${this.$formatter.pad(i + 1)}`, text: this.$d(d, 'month') })
      }
      return options
    },
    gradeOptions() {
      return this.$store.getters.gradeOptions
    },
    // permission / setting
    canUpgrade() {
      if (!this.$validate.allowAction('upgradeStudent', this.userType)) return false

      if (this.gEnableCustomGrade) return this.enableUpgrade

      return !this.gDisableStudentUpgrade
    },
    canCreate() {
      return this.$validate.allowAction('CreateStudent', this.userType)
    },
    usernameRequired() {
      return this.isAppMode(['T01'])
    },
    enableDynamicField() {
      return this.isAppMode(['DEV', 'UAT', 'T25', 'T40', 'T45', '59+'])
    },
  },
  data: vm => ({
    count: 0,
    userType: vm.gfGetUserType(),
    tableLoading: false,
    tableData: [],
    tableItemTotal: 0,
    advancedDialogOpen: false,
    exportLoading: false,
    enableUpgrade: false,

    filter: {
      sortBy: [],
      sortDesc: [true],
      /* 進階搜尋 */
      grade: '',
      school: '',
      regYearMonth: '',
      birth: '',
    },
    filterSelected: {
      sortBy: '',
      sortDesc: true,
      status: 'active',
      keyword: '',
      searchType: 'name',
      page: 1,
      limit: 10,
      // 禁咗搜尋之後save
      grade: '',
      school: '',
      regYearMonth: '',
      birth: '',
    },

    quitStudentTotal: 0,
    activeStudentTotal: 0,

    fieldList: [],

    fieldFormList: [],

    // --- static data
    icon: {
      mdiArrowUpCircleOutline,
      mdiDragHorizontalVariant,
    },
    statusOptions: [
      { value: 'active', text: vm.$t('studentStatus.active') },
      { value: 'quit', text: vm.$t('studentStatus.quit') },
    ],
    fieldTypeOptions: [
      { value: 'freetext', text: vm.$t('fieldType.freetext') },
      { value: 'dropdown', text: vm.$t('fieldType.dropdown') },
    ],
  }),
  methods: {
    async _getPayload() {
      let payload = {
        filter_limit: this.filterSelected.limit,
        filter_page: this.filterSelected.page - 1,
        join_school_data: this.isAppMode(['T21']),
        filter_item: [],
      }

      if (!this.gDisableStudentCenterFilter) {
        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) payload['center_id'] = selectedSchool
      }

      if (this.$validate.DataValid(this.filterSelected.keyword)) {
        const keyword = this.filterSelected.keyword.trim()

        if (this.$validate.DataValid(this.filterSelected.searchType)) {
          if (this.filterSelected.searchType === 'id') {
            if (this.$validate.regexId(keyword)) {
              const id = keyword.substring(1)
              payload['filter_item'].push({ key: 'id', value: parseInt(id) })
            } else if (this.$validate.regexNumber(keyword)) {
              payload['filter_item'].push({ key: 'id', value: parseInt(keyword) })
            }
          } else {
            payload['filter_item'].push({ key: this.filterSelected.searchType, value: keyword })
          }
        } else {
          payload['search'] = keyword
        }
      }
      if (this.$validate.DataValid(this.filterSelected.grade)) {
        payload['filter_item'].push({ key: 'grade', value: this.filterSelected.grade })
      }
      if (this.$validate.DataValid(this.filterSelected.school)) {
        payload['filter_item'].push({ key: 'school', value: this.filterSelected.school })
      }
      if (this.$validate.DataValid(this.filterSelected.regYearMonth)) {
        payload['filter_item'].push({ key: 'create_date', value: this.filterSelected.regYearMonth })
      }
      if (this.$validate.DataValid(this.filterSelected.status)) {
        payload['filter_item'].push({ key: 'status', value: this.filterSelected.status })
      }
      if (this.$validate.DataValid(this.filterSelected.birth)) {
        payload['filter_item'].push({ key: 'birthday', value: `-${this.filterSelected.birth}-` })
      }

      if (this.filterSelected.sortBy) {
        switch (this.filterSelected.sortBy) {
          case 'student_school_name':
            payload['order_by'] = 'school'
            break
          case 'reg_date':
            payload['order_by'] = 'create_date'
            break
          default:
            payload['order_by'] = this.filterSelected.sortBy
            break
        }

        if (this.filterSelected.sortDesc) {
          payload['sort_dir'] = 'desc'
        } else {
          payload['sort_dir'] = 'asc'
        }
      }

      return payload
    },
    async getStudentData(regenUrl = true) {
      if (regenUrl === true) this._regenerateURL()

      this.tableLoading = true
      try {
        const payload = await this._getPayload()

        const { data, total } = await this.$Fetcher.GetStudents(payload)
        this.tableData = data.map(el => {
          return {
            ...el,
            id: el.id,
            score: el.belt,
            center_name: this.$t(el.center_name),
            age: this._getAge(el.birthday),
          }
        })

        this.tableItemTotal = total
        this.tableLoading = false
      } catch (err) {
        this.$common.error(err)
        this.tableData = []
        if (this.filterSelected.page === 1) {
          this.tableItemTotal = 0
        }
        this.tableLoading = false

        if (err === 'abort') {
          return false
        }
      }

      return true
    },
    _getAge(birthday) {
      if (this.$validate.DataValid(birthday) && this.$validate.regexDateStr(birthday)) {
        const birth = this.$formatter.convertDate(birthday)
        const today = this.$formatter.convertDate()
        let age = Math.abs(today.getFullYear() - birth.getFullYear())
        if (
          birth.getMonth() > today.getMonth() ||
          (birth.getMonth() === today.getMonth() && birth.getDate() > today.getDate())
        ) {
          age = age - 1
        }
        return age
      }

      return ''
    },
    search() {
      this.filterSelected.page = 1
      this.tableItemTotal = 0
      this.getStudentData()
    },
    searchKeyword(keyword) {
      this.filterSelected.keyword = keyword.trim()
      this.search()
    },
    clearSearch() {
      this._clearFilter()
      this.search()
    },
    refreshData() {
      this.filterSelected.limit = 10
      this.search()
    },
    onTableOptionsChange(options) {
      this.filterSelected.sortBy = ''
      this.filterSelected.sortDesc = true
      if (options.sortBy.length && options.sortDesc.length) {
        this.filterSelected.sortBy = options.sortBy[0]
        this.filterSelected.sortDesc = options.sortDesc[0]
      }

      this.getStudentData()
    },
    _clearFilter() {
      this.filterSelected.page = 1
      this.tableItemTotal = 0
      this.filterSelected.keyword = ''
      this.filterSelected.searchType = this.searchTypeOptions[0].value
      this.filterSelected.grade = ''
      this.filterSelected.school = ''
      this.filterSelected.regYearMonth = ''
      this.filterSelected.birth = ''
      this.filterSelected.sortBy = ''
      this.filterSelected.sortDes = true
      this.filter.sortBy = []
      this.filter.sortDesc = [true]
    },
    // -- (進階搜尋)
    async handleAdvancedSearch(allow) {
      if (!allow) return
      if (this.tableLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      this.filter.grade = this.filterSelected.grade
      this.filter.school = this.filterSelected.school
      this.filter.regYearMonth = this.filterSelected.regYearMonth
      this.filter.birth = this.filterSelected.birth

      const confirm = await this.$refs.advancedFormDialog.show()
      if (confirm) {
        this.filterSelected.grade = this.filter.grade
        this.filterSelected.school = this.filter.school
        this.filterSelected.regYearMonth = this.filter.regYearMonth
        this.filterSelected.birth = this.filter.birth
        this.$refs.advancedFormDialog.hide()
        this.search()
      }
    },

    _getQuery() {
      this._clearFilter()
      this.filterSelected.status = 'active'
      this.filterSelected.limit = 10

      const query = this.gfGetTableQuery({
        searchType: 'string',
        search: 'string',
        page: 'int',
        limit: 'int',
        grade: 'string',
        school: 'string',
        date: 'year_month',
        status: 'string',
        birth: 'string',
        sort: 'string',
        sort_order: 'string',
      })
      for (const key in this.filterSelected) {
        if (key === 'keyword') {
          if (this.$validate.DataValid(query['search'])) {
            this.$set(this.filterSelected, key, query['search'])
          }
        } else if (key === 'regYearMonth') {
          if (this.$validate.DataValid(query['date'])) {
            this.$set(this.filterSelected, key, query['date'])
          }
        } else if (key === 'sortBy') {
          if (this.$validate.DataValid(query['sort'])) {
            this.$set(this.filterSelected, key, query['sort'])
            this.$set(this.filter, key, [query.sort])

            if (this.$validate.DataValid(query['sort_order'])) {
              if (query.sort_order === 'asc') {
                this.$set(this.filterSelected, 'sortDesc', false)
                this.$set(this.filter, 'sortDesc', [false])
              }
            }
          }
        } else if (this.$validate.DataValid(query[key]) && key !== 'sort_order') {
          this.$set(this.filterSelected, key, query[key])
        }
      }
    },
    _regenerateURL() {
      const q = {
        searchType: this.filterSelected.searchType,
        search: this.filterSelected.keyword.trim(),
        page: this.filterSelected.limit !== 10 || this.filterSelected.page !== 1 ? this.filterSelected.page : null,
        limit: this.filterSelected.limit !== 10 || this.filterSelected.page !== 1 ? this.filterSelected.limit : null,
        grade: this.filterSelected.grade,
        school: this.filterSelected.school,
        date: this.filterSelected.regYearMonth,
        birth: this.filterSelected.birth,
        status: this.filterSelected.status,
        sort: this.filterSelected.sortBy,
        sort_order: this.filterSelected.sortBy ? (this.filterSelected.sortDesc ? 'desc' : 'asc') : null,
      }
      const newQuery = this.$common.diffQuery(q, this.$route.query)
      if (newQuery) this.$router.replace({ query: newQuery })
    },

    async _getSetting() {
      if (this.gEnableCustomGrade) {
        try {
          const data = await this.$Fetcher.GetSetting()
          this.enableUpgrade = data.upgrade_setting || false
        } catch {}
      }
    },

    _getFields() {
      this.fieldList = []
      fields.forEach(item => {
        let show = true
        if (
          item.hide_import ||
          (this.$validate.DataValid(item.modes, true) && !this.isAppMode(item.modes)) ||
          (this.$validate.DataValid(item.except_modes, true) && this.isAppMode(item.except_modes)) ||
          (item.key === 'grade' && this.gDisableStudentGrade) ||
          (item.key === 'username' && this.gDisableStudentAccount) ||
          (item.key === 'password' && this.gDisableStudentAccount)
        ) {
          show = false
        }

        if (item.key === 'username' || item.key === 'password') {
          if (this.usernameRequired) {
            item.required = true
          } else {
            item.required = false
          }
        }

        if (show) {
          this.fieldList.push(item)
        }
      })
    },

    // ========= 學生升級 =========
    async handleUpgradeStudent() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      const confirm = await this.confirmRef.show({
        title: 'message.confirmUpgrade',
        message: 'message.confirmUpgradeMsg',
      })
      if (confirm) {
        this.$store.dispatch('setLoading', true)
        try {
          if (this.gEnableCustomGrade) {
            await this.$Fetcher.UpgradeStudentsWithCustomGrade()
          } else {
            await this.$Fetcher.UpgradeStudents()
          }
          this.$store.dispatch('toggleSnack', {
            message: 'message.saveSuccess',
            type: 'success',
          })
          this.clearSearch()
        } catch (errMsg) {
          this.showAlert({
            title: 'message.saveFail',
            message: errMsg,
            isError: true,
          })
        } finally {
          this.$store.dispatch('setLoading', false)
        }
      }
    },

    // ========= 匯出報表 =========
    async exportData() {
      if (this.exportLoading || this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      this.exportLoading = true

      try {
        let header = {
          belt: this.$t('belt'),
          student_code: this.$t('studentCode'),
          grade: this.$t('grade'),
          age: this.$t('age'),
          display_student_name: this.$t('name'),
          student_school_name: this.$t('studentSchool'),
          phone: this.$t('phone'),
          birthday: this.$t('birthday'),
          reg_date: this.$t('regDate'),
          status: this.$t('enrollStatus'),
          center_name: this.$t('campus'),
        }
        if (this.gDisableStudentGrade) {
          delete header['grade']
        }
        if (this.isAppMode(['T27'])) {
          delete header['student_school_name']
        }
        if (!this.isAppMode(['T21'])) {
          delete header['center_name']
        }
        if (!this.isAppMode(['T01'])) {
          delete header['belt']
          delete header['age']
        }
        if (!this.isAppMode(['T11'])) {
          delete header['birthday']
        }

        let payload = await this._getPayload()
        delete payload['filter_limit']
        delete payload['filter_page']

        const { data } = await this.$Fetcher.GetStudents(payload)
        const exportData = data.map(el => {
          const foundGrade = this.gradeOptions.find(item => item.value === el.grade)
          return {
            ...el,
            grade: foundGrade ? foundGrade.text : el.grade,
            status: el.status === 'active' ? this.$t('studentStatus.active') : this.$t('studentStatus.quit'),
            belt: el.belt ? this.$t(`beltOptions.${el.belt}`) : '',
            center_name: this.$t(el.center_name),
            age: this._getAge(el.birthday),
          }
        })

        this.exportExcel(
          header,
          exportData,
          this.$t('menu.student'),
          `student_${this.$formatter.formatDate().replace(/-/g, '')}.xlsx`,
        )
      } catch (err) {
        this.$common.error(err)
        if (err !== 'abort') {
          this.$store.dispatch('toggleSnack', {
            message: 'message.exportFail',
            type: 'error',
          })
        }
      } finally {
        this.exportLoading = false
      }
    },

    // ========= 匯入資料 =========
    async importData(file) {
      const check = await this.gfCheckSchoolSelected()
      if (!check) return

      const confirm = await this.confirmRef.show({
        title: 'message.confirmToImport',
        message: 'message.confirmToImportMsg',
      })
      if (confirm) {
        try {
          const data = await this.importExcel(file)
          let formattedData = []
          if (this.$validate.DataValid(data, true)) {
            if (data[0]['phone'] === undefined) {
              if (data.length > 1) {
                for (let i = 1; i < data.length; i++) {
                  let obj = {}
                  for (let key in data[i]) {
                    if (data[0][key]) {
                      obj[data[0][key]] = data[i][key]
                    } else {
                      obj[key] = data[i][key]
                    }
                  }
                  formattedData.push(obj)
                }
              }
            } else {
              formattedData = data
            }
          }

          if (formattedData.length) {
            let customFieldList = []
            try {
              const data = await this.$Fetcher.GetSetting()
              if (this.$validate.DataValid(data.student_field, true)) {
                customFieldList = data.student_field
              }
            } catch {}
            let required = {}
            this.fieldList.forEach(item => {
              if (item.required) required[item.key] = true
            })
            customFieldList.forEach(item => {
              if (item.is_required) required[item.field_key] = true
            })
            if (this.isAppMode(['T27']) && required['student_name']) {
              required['nickname'] = true
            }
            formattedData.forEach(item => {
              for (let key in required) {
                if (!this.$validate.DataValid(item[key])) {
                  required[key] = false
                }
              }
            })
            if (this.isAppMode(['T27'])) {
              if (!required['nickname'] && required['student_name']) {
                required['nickname'] = true
              }
              if (!required['student_name'] && required['nickname']) {
                required['student_name'] = true
              }
            }
            const invalid = []
            for (let key in required) {
              if (!required[key]) {
                invalid.push(key)
              }
            }

            if (invalid.length) {
              throw `${this.$t('message.required')} : ${invalid.join(',')}`
            } else {
              let school_id = this.gfGetUserSelectedSchool()

              const students = []
              formattedData.forEach(item => {
                let itemObj = {}
                const keys = Object.keys(item)
                for (let i = 0; i < keys.length; i++) {
                  const key = keys[i] ? keys[i].trim().toLowerCase() : ''
                  if (key && this.$validate.DataValid(item[key])) {
                    const foundField = this.fieldList.find(el => el.key === key)
                    if (foundField) {
                      if (key === 'username' && this.isAppMode(['T27'])) {
                        itemObj['username'] = item.username.toString()
                        itemObj['email'] = item.username.toString()
                      } else if (key === 'email' && this.isAppMode(['T27'])) {
                        itemObj['username'] = item.email.toString()
                        itemObj['email'] = item.email.toString()
                      } else if (key === 'enroll_reason') {
                        const value = item[key].trim().toLowerCase()
                        let options = this.gfGetOptionsByKey(key)
                        options = options.filter(el => el.value !== 'others')
                        const foundValue = options.find(el => {
                          return el.value === value || el.text.toLowerCase() === value
                        })
                        if (foundValue) {
                          itemObj[key] = foundValue.value
                        } else {
                          itemObj[key] = 'others'
                          itemObj['enroll_reason_other'] = item[key]
                        }
                      } else if (key === 'know_from') {
                        const value = item[key].trim().toLowerCase()
                        let options = this.gfGetOptionsByKey(key)
                        options = options.filter(el => el.value !== 'other')
                        const foundValue = options.find(el => {
                          return el.value === value || el.text.toLowerCase() === value
                        })
                        if (foundValue) {
                          itemObj[key] = [foundValue.value]
                        } else {
                          itemObj[key] = [item[key]]
                        }
                      } else if (key === 'grade' && this.gEnableCustomGrade) {
                        const value = item[key].trim()
                        const options = this.gfGetOptionsByKey(key)
                        const foundValue = options.find(el => el.text === value)
                        if (foundValue) {
                          itemObj[key] = foundValue.value
                        } else {
                          itemObj[key] = item[key]
                        }
                      } else if (foundField.type === 'date') {
                        if (this.$validate.regexDateStr(item[key])) {
                          itemObj[key] = item[key]
                        } else {
                          const old = item[key] - 1
                          const t = Math.round((old - Math.floor(old)) * 24 * 60 * 60)
                          const time = new Date(1900, 0, old, 0, 0, t)
                          itemObj[key] = this.$formatter.formatDate(time)
                        }
                      } else if (foundField.type === 'boolean') {
                        const value = item[key].trim().toUpperCase()
                        itemObj[key] = value === 'Y' ? true : false
                      } else if (foundField.is_select) {
                        const value = item[key].trim().toLowerCase()
                        const options = this.gfGetOptionsByKey(key)
                        const foundValue = options.find(el => {
                          return el.value === value || el.text.toLowerCase() === value
                        })
                        if (foundValue) {
                          itemObj[key] = foundValue.value
                        } else {
                          itemObj[key] = item[key]
                        }
                      } else {
                        if (foundField.type !== 'number' && typeof item[key] !== 'string') {
                          itemObj[key] = item[key].toString()
                        } else {
                          itemObj[key] = item[key]
                        }
                      }
                    } else {
                      const foundInCustom = customFieldList.find(el => el.field_key === key)
                      if (foundInCustom) {
                        if (foundInCustom.field_type === 'dropdown') {
                          if (foundInCustom.is_multiple) {
                            const splits = item[key].trim().split(',')
                            const values = []
                            splits.forEach(val => {
                              const value = val.trim().toLowerCase()
                              if (this.$validate.DataValid(value)) {
                                const foundValue = foundInCustom.options.find(el => {
                                  return (
                                    el.option_value === value ||
                                    el.option_name_zh.trim().toLowerCase() === value ||
                                    el.option_name_en.trim().toLowerCase() === value
                                  )
                                })
                                if (foundValue) {
                                  values.push(foundValue.option_value)
                                } else {
                                  values.push(val)
                                }
                              }
                            })
                            itemObj[key] = values
                          } else {
                            const value = item[key].trim().toLowerCase()
                            const foundValue = foundInCustom.options.find(el => {
                              return (
                                el.option_value === value ||
                                el.option_name_zh.trim().toLowerCase() === value ||
                                el.option_name_en.trim().toLowerCase() === value
                              )
                            })
                            if (foundValue) {
                              itemObj[key] = foundValue.option_value
                            } else {
                              itemObj[key] = item[key]
                            }
                          }
                        } else {
                          if (typeof item[key] !== 'string') {
                            itemObj[key] = item[key].toString()
                          } else {
                            itemObj[key] = item[key]
                          }
                        }
                      } else {
                        if (key === 'parent_name' && !this.isAppMode(['T40'])) {
                          itemObj['contact_person_1_name'] = item.parent_name.toString()
                        } else if (key === 'parent_phone') {
                          itemObj['contact_person_1_phone'] = item.parent_phone.toString()
                        } else if (key === 'agent_code') {
                          itemObj['student_code'] = item.agent_code.toString()
                        } else if (key === 'upline_agent_code') {
                          itemObj['upline_student_code'] = item.upline_agent_code.toString()
                        } else if (key === 'name_en') {
                          itemObj['student_name_en'] = item.name_en.toString()
                        } else if (key === 'nickname' && this.isAppMode(['T27'])) {
                          itemObj['student_name'] = item.nickname.toString()
                        } else if (key === 'email' && this.isAppMode(['T27'])) {
                          itemObj['username'] = item.email
                          itemObj['email'] = item.email
                        } else if (key === 'sportify_women_empower_women') {
                          const value = item[key].trim().toUpperCase()
                          itemObj[key] = value === 'Y' ? true : false
                        } else {
                          if (typeof item[key] !== 'string') {
                            itemObj[key] = item[key].toString()
                          } else {
                            itemObj[key] = item[key]
                          }
                        }
                      }
                    }
                  }
                }

                if (this.gEnableStudentMultiCenter) {
                  itemObj['schools'] = [school_id]
                  itemObj['school_id'] = -1
                } else {
                  itemObj['school_id'] = school_id
                }

                if (!this.$validate.DataValid(itemObj.username)) {
                  itemObj.username = ''
                }
                if (!this.$validate.DataValid(itemObj.password)) {
                  itemObj.password = ''
                }
                if (!this.$validate.DataValid(itemObj.username) && !this.usernameRequired) {
                  itemObj['generate_username'] = true
                }
                if (!this.$validate.DataValid(itemObj.password) && !this.usernameRequired) {
                  itemObj['password'] = itemObj['phone'] || ''
                }

                students.push(itemObj)
              })

              const { result, data: returnData } = await this.$Fetcher.ImportStudent(students)
              if (result === 'success') {
                this.$store.dispatch('toggleSnack', {
                  message: 'message.importSuccess',
                  type: 'success',
                })
              } else {
                this.showAlert({
                  title: 'message.importStudentFail',
                  message: returnData.join('<br>'),
                  isError: true,
                })
              }
              this.clearSearch()
            }
          } else {
            throw 'message.noImportData'
          }
        } catch (err) {
          this.showAlert({
            title: 'message.importFail',
            message: err,
            isError: true,
          })
        } finally {
          this.$store.dispatch('setLoading', false)
        }
      }
    },

    // ========= 現有學生數量 & 已退學學生數量 =========
    async _getQuitStudentTotal() {
      try {
        const payload = {
          filter_limit: 1,
          filter_page: 0,
          filter_item: [{ key: 'status', value: 'quit' }],
        }

        if (!this.gDisableStudentCenterFilter) {
          const selectedSchool = this.gfGetUserSelectedSchool()
          if (selectedSchool > -1) payload['center_id'] = selectedSchool
        }

        const { total } = await this.$Fetcher.GetStudents(payload)
        this.quitStudentTotal = total
      } catch (err) {
        this.$common.error(err)
        if (err === 'abort') {
          return false
        }
      }

      return true
    },
    async _getActiveStudentTotal() {
      try {
        const payload = {
          filter_limit: 1,
          filter_page: 0,
          filter_item: [{ key: 'status', value: 'active' }],
        }
        if (!this.gDisableStudentCenterFilter) {
          const selectedSchool = this.gfGetUserSelectedSchool()
          if (selectedSchool > -1) payload['center_id'] = selectedSchool
        }

        const { total } = await this.$Fetcher.GetStudents(payload)
        this.activeStudentTotal = total
      } catch (err) {
        this.$common.error(err)
        if (err === 'abort') {
          return false
        }
      }

      return true
    },

    // ========= 生成匯入CSV =========
    async genImportCVS() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      this.$store.dispatch('setLoading', true)
      let header = {}
      let keyHeader = {}
      this.fieldList.forEach(item => {
        let labelProps = item.label_props || {}
        if (
          item.key === 'contact_person_1_name' ||
          item.key === 'contact_person_1_phone' ||
          item.key === 'contact_person_1_email'
        ) {
          labelProps = { number: 1, relationship: this.$t(this.isAppMode(['T21']) ? 'parent' : 'father') }
        } else if (
          item.key === 'contact_person_2_name' ||
          item.key === 'contact_person_2_phone' ||
          item.key === 'contact_person_2_email'
        ) {
          labelProps = { number: 2, relationship: this.$t(this.isAppMode(['T21']) ? 'emergency' : 'mother') }
        } else if (
          item.key === 'contact_person_3_name' ||
          item.key === 'contact_person_3_phone' ||
          item.key === 'contact_person_3_email'
        ) {
          labelProps = { number: 3, relationship: this.$t('emergency') }
        }
        let label = this.$t(item.label, labelProps)
        if (item.required) {
          label += '*'
        }
        if (item.type === 'date') {
          label += ' (YYYY-MM-DD)'
        }
        if (item.label_des) {
          label += item.label_des
        }
        header[item.key] = label
        keyHeader[item.key] = item.key
      })

      try {
        const data = await this.$Fetcher.GetSetting()
        if (this.$validate.DataValid(data.student_field, true)) {
          data.student_field.forEach(item => {
            let label = this.$i18n.locale === 'en' ? item.field_name_en : item.field_name_zh
            if (item.is_required) {
              label += '*'
            }
            header[item.field_key] = label
            keyHeader[item.field_key] = item.field_key
          })
        }
      } catch {}

      this.exportExcel(header, [keyHeader], this.$t('menu.student'), `${this.$t('menu.student')}_import.csv`, true)
      this.$store.dispatch('setLoading', false)
    },

    // ========= 修改學生表格 =========
    async handleEditStudentForm(newOpen = true) {
      if (newOpen) {
        if (this.$store.getters.isLoading) {
          this.$store.dispatch('toggleSnack', { message: 'processing' })
          return
        }

        this.fieldFormList = []

        this.$store.dispatch('setLoading', true)
        try {
          const data = await this.$Fetcher.GetSetting()
          if (this.$validate.DataValid(data.student_field, true)) {
            for (let i = 0; i < data.student_field.length; i++) {
              this.count += 1
              let obj = {
                id: this.count,
                ...data.student_field[i],
              }

              obj['options'] = (data.student_field[i].options || []).map(opt => {
                this.count += 1
                return {
                  id: this.count,
                  ...opt,
                }
              })
              this.fieldFormList.push(obj)
            }
          }
        } catch {}
        this.$store.dispatch('setLoading', false)
      } else {
        this.$refs.editStudentFormDialog.setLoading(false)
      }

      const confirm = await this.$refs.editStudentFormDialog.show()
      if (confirm) {
        let pass = true
        for (let i = 0; i < this.fieldFormList.length; i++) {
          if (
            this.fieldFormList[i].field_type === 'dropdown' &&
            !this.$validate.DataValid(this.fieldFormList[i].options, true)
          ) {
            pass = false
            break
          }
        }
        if (!pass) {
          this.$store.dispatch('toggleSnack', { type: 'error', message: 'message.selectShouldHaveOption' })
          this.handleEditStudentForm(false)
          return
        }
        try {
          const list = this.fieldFormList.map(item => {
            let obj = {
              field_key: item.field_key || '',
              field_name_zh: item.field_name_zh,
              field_name_en: item.field_name_en,
              field_type: item.field_type,
              is_multiple: item.field_type === 'dropdown' ? item.is_multiple || false : false,
              is_required: item.is_required || false,
              options: [],
            }

            if (obj.field_type === 'dropdown') {
              obj['options'] = item.options.map(opt => {
                return {
                  option_name_zh: opt.option_name_zh,
                  option_name_en: opt.option_name_en,
                  option_value: opt.option_name_zh,
                }
              })
            }

            return obj
          })
          await this.$Fetcher.UpdateSetting({ student_field: list })
          this.$store.dispatch('toggleSnack', {
            message: 'message.saveSuccess',
            type: 'success',
          })
          this.$refs.editStudentFormDialog.hide()
        } catch (err) {
          this.showAlert({
            title: 'message.saveFail',
            message: err,
            isError: true,
          })
          this.handleEditStudentForm(false)
        }
      }
    },
    addNewField() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      this.count += 1
      this.fieldFormList.push({
        id: this.count,
        field_type: '',
        field_name_zh: '',
        field_name_en: '',
        options: [],
        is_multiple: false,
        is_required: false,
      })
    },
    removeField(id) {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      const foundPos = this.fieldFormList.findIndex(el => el.id === id)
      this.fieldFormList.splice(foundPos, 1)
    },
    addNewOption(id) {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      for (let i = 0; i < this.fieldFormList.length; i++) {
        if (this.fieldFormList[i].id === id) {
          this.count += 1
          this.fieldFormList[i].options.push({
            id: this.count,
            option_name_zh: '',
            option_name_en: '',
          })
          break
        }
      }
    },
    removeOption(parentId, id) {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      for (let i = 0; i < this.fieldFormList.length; i++) {
        if (this.fieldFormList[i].id === parentId) {
          const foundPos = this.fieldFormList[i].options.findIndex(el => el.id === id)
          this.fieldFormList[i].options.splice(foundPos, 1)
          break
        }
      }
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: StudentList')
      this.userType = this.gfGetUserType()
      await this._getSetting()
      this._getFields()
      this._getQuery()
      this.$store.dispatch('toggleLoadingPage', false)

      let next = await this.getStudentData(false)

      if (
        this.filterSelected.status === 'active' &&
        !this.filterSelected.keyword &&
        !this.filterSelected.grade &&
        !this.filterSelected.school &&
        !this.filterSelected.regYearMonth &&
        !this.filterSelected.birth
      ) {
        this.activeStudentTotal = this.tableItemTotal
      } else {
        if (next) next = await this._getActiveStudentTotal()
      }

      if (
        this.filterSelected.status === 'quit' &&
        !this.filterSelected.keyword &&
        !this.filterSelected.grade &&
        !this.filterSelected.school &&
        !this.filterSelected.regYearMonth &&
        !this.filterSelected.birth
      ) {
        this.quitStudentTotal = this.tableItemTotal
      } else {
        if (next) await this._getQuitStudentTotal()
      }
    },
  },

  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.tableLoading = true
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    this.$XHR.abort()
    next()
  },
}
</script>

<style lang="scss" scoped>
::v-deep.upgrade-checkbox {
  & label {
    color: var(--v-colorBlack-base);
    font-weight: 500;
    font-size: 0.875rem;
  }
}
</style>
